import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageFlip } from 'page-flip';
import {
  listContentItems,
  fetchCoverPreviewHtml,
  fetchInteriorPagePreviewHtml,
  changeFormatOnly,
  getPrintQualityCheck,
  listPages,
  fetchBookRow,
  updateBookRow
} from '../../services/compositionApi';
import { listPrintFormats } from '../../services/ordersApi';
import { applyLifecycleStatus } from '../../utils/bookLifecycle';
import { formatPriceCents, formatPriceCentsDelta as formatEurosDelta } from '../../utils/orderWorkflow';
import {
  FLIP_COVER_INDEX,
  buildFlipEntries,
  contentPageFromFlipIndex,
  describeContentSpread
} from '../../utils/flipBookGeometry';
import PagePreviewViewer from '../common/PagePreviewViewer';
import { getPageMetrics } from './atelier/atelierLayoutGeometry';
import SpreadPhotoInfo from '../common/SpreadPhotoInfo';
import PrintQualityRecapModal from '../common/PrintQualityRecapModal';
import { avertissementsDejaAcquittes, acquitterAvertissementsQualite } from '../../utils/qualityAcknowledgment';
import { fitFlaggedPhotosWhole } from '../../utils/fixFlaggedPhotos';
import '../../styles/luxe-theme.css';
import './BookPreviewFinalLuxe.css';
import { isCatalogFormat, formatDimensions, listCatalogFormats, formatCm, catalogFormatName, gutterSafetyMmOf } from '../../utils/printFormats';

// Etape "JE VERIFIE" du parcours (voir cahier des charges : JE CREE -> JE
// VERIFIE -> J'ACHETE) — un seul ecran qui feuillette le livre reel
// (couverture -> pages -> 4e, meme moteur que le PDF final) et porte, en
// permanence a cote, le choix du FORMAT et de la PAGINATION avec le prix en
// direct. Le contenu ne change jamais ici — "Modifier mon livre" renvoie a
// l'atelier, seul endroit qui touche au contenu.
//
// FEUILLETAGE REEL (2026-10-11, retour utilisateur : "si on peut feuilletter
// le rendu comme dans Exemples ça serait top") : le plateau central utilise
// desormais page-flip, la meme bibliotheque et le meme schema d'integration
// IMPERATIVE que ExemplesLuxe.js (voir son commentaire d'en-tete — page-flip
// prend possession directe du DOM de ses pages, incompatible avec le cycle
// de rendu React a l'interieur de la zone qu'elle gere). Difference majeure
// avec Exemples : ici le contenu de chaque page vient du MEME moteur de
// rendu que le PDF final (fetchCoverPreviewHtml/fetchInteriorPagePreviewHtml,
// un appel HTTP par page), pas de donnees locales instantanees — toutes les
// pages sont donc PRECHARGEES en une fois (voir pagesByKey plus bas) avant de
// construire le livre feuilletable, avec un etat de chargement le temps que
// ca arrive.

// Dimensions alignees sur backend/services/composition/coverFormat.js
// (COVER_FORMATS) — a garder synchronisees a la main si l'un des deux change.
// widthMm/heightMm servent a calculer la taille NATURELLE reelle de chaque
// page (voir naturalPageWidthPx/HeightPx plus bas) : le rendu envoye a
// l'iframe (routes/composition.js: renderSinglePageHtml) etire toujours la
// page en 100vw/100vh — c'est la taille du CONTENEUR qui doit porter le vrai
// ratio du format, sinon changer de format ne change jamais rien a l'ecran
// (bug deja signale et corrige). `spine` pilote uniquement le visuel de
// tranche cote client (voir .preview-flip-page-spine dans le CSS) — aucune
// donnee serveur equivalente, purement descriptif (souple/rigide/premium).
const PRINT_FORMATS = [
  { id: 'livret', dimensions: '20 × 20 cm', widthMm: 200, heightMm: 200, spine: 'soft' },
  { id: 'standard', badge: '⭐', dimensions: '21 × 28 cm', widthMm: 210, heightMm: 280, spine: 'soft' },
  { id: 'luxe', dimensions: '21 × 28 cm', widthMm: 210, heightMm: 280, spine: 'premium' }
];

const normalizePrintFormat = (value) => (
  PRINT_FORMATS.some((format) => format.id === value) || isCatalogFormat(value) ? value : 'standard'
);

// Conversion physique standard (96dpi de reference) — meme constante que
// l'atelier (AtelierBookView.js).
const PX_PER_MM = 96 / 25.4;

// Limite le nombre de requetes de rendu lancees en meme temps (un livre de
// 100+ pages ne doit pas ouvrir 100 connexions simultanees) — simple
// decoupage en lots, pas de dependance supplementaire pour si peu.
async function mapWithConcurrency(items, limit, mapper) {
  const results = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await mapper(items[index], index);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// Regle de parite (page 1 seule a droite, contre-plats avant/arriere) et
// construction de la sequence de feuilles : voir utils/flipBookGeometry.js
// — partagee avec l'Atelier, qui reutilise ce meme feuilletage.

// --- Plateau de feuilletage (page-flip) -----------------------------------
//
// Port IMPERATIF, meme principe que ExemplesLuxe.js (voir son en-tete) :
// page-flip prend possession directe du DOM de ses pages des loadFromHTML et
// les anime lui-meme hors du cycle de rendu React — aucun composant React
// (meme pas l'ancien ScaledPageFrame) ne doit vivre A L'INTERIEUR du
// conteneur qu'on lui passe. Chaque feuille recoit un <iframe> a sa taille
// NATURELLE reelle (naturalWidthPx/HeightPx), mis a l'echelle par un
// transform:scale() calcule a la main via ResizeObserver — meme raison que
// l'ancien ScaledPageFrame (unites mm/pt absolues dans le CSS de la page,
// qui debordent/se decalent si l'iframe est redimensionnee directement).
function FlipStage({ pagesByKey, totalPages, naturalWidthPx, naturalHeightPx, spineClass, onFlipChange, apiRef }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !pagesByKey) return undefined;

    container.innerHTML = '';
    const book = document.createElement('div');
    const entries = buildFlipEntries(totalPages);
    const resizeObservers = [];

    entries.forEach((entry) => {
      const pageEl = document.createElement('div');
      const isCoverLike = entry.kind === 'cover' || entry.kind === 'back-cover';
      pageEl.className = `preview-flip-page${isCoverLike ? ` is-cover ${spineClass}` : ''}${entry.kind === 'contreplat' ? ' preview-flip-page-contreplat' : ''}`;
      pageEl.dataset.density = isCoverLike ? 'hard' : 'soft';

      const html = entry.kind === 'cover' ? pagesByKey.cover
        : entry.kind === 'back-cover' ? pagesByKey.backCover
          : entry.kind === 'interior' ? pagesByKey.interior?.[entry.pageIndex]
            : null;

      if (html) {
        const clip = document.createElement('div');
        clip.className = 'preview-flip-page-clip';
        const iframe = document.createElement('iframe');
        iframe.title = entry.kind === 'interior' ? `page-${entry.pageIndex}` : entry.kind;
        iframe.srcdoc = html;
        iframe.className = 'preview-flip-frame';
        iframe.style.width = `${naturalWidthPx}px`;
        iframe.style.height = `${naturalHeightPx}px`;
        clip.appendChild(iframe);
        pageEl.appendChild(clip);

        const applyScale = () => {
          const rect = clip.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;
          const scale = Math.min(rect.width / naturalWidthPx, rect.height / naturalHeightPx);
          iframe.style.transform = `scale(${scale})`;
        };
        applyScale();
        const observer = new ResizeObserver(applyScale);
        observer.observe(clip);
        resizeObservers.push(observer);
      }

      if (isCoverLike) {
        const spine = document.createElement('span');
        spine.className = 'preview-flip-page-spine';
        pageEl.appendChild(spine);
      }

      book.appendChild(pageEl);
    });
    container.appendChild(book);

    // Largeur logique de reference pour page-flip ("stretch" la redimensionne
    // ensuite a l'espace reellement disponible) — meme principe qu'Exemples
    // (bw=400 la-bas), ici derivee du ratio reel du format choisi.
    const logicalWidth = 420;
    const pf = new PageFlip(book, {
      width: logicalWidth,
      height: Math.round(logicalWidth * (naturalHeightPx / naturalWidthPx)),
      size: 'stretch',
      minWidth: 220,
      maxWidth: 1500,
      minHeight: 260,
      maxHeight: 1900,
      showCover: true,
      maxShadowOpacity: 0.4,
      mobileScrollSupport: false,
      flippingTime: 650,
      usePortrait: true,
      startPage: 0,
      autoSize: true,
      drawShadow: true
    });
    pf.loadFromHTML(book.querySelectorAll('.preview-flip-page'));

    const reportState = () => {
      onFlipChange({ index: pf.getCurrentPageIndex(), orientation: pf.getOrientation(), pageCount: entries.length });
    };
    pf.on('flip', reportState);
    pf.on('changeOrientation', reportState);
    reportState();

    if (apiRef) {
      apiRef.current = {
        flipNext: () => pf.flipNext('bottom'),
        flipPrev: () => pf.flipPrev('bottom'),
        turnToPage: (n) => pf.turnToPage(n),
        pageCount: entries.length
      };
    }

    return () => {
      resizeObservers.forEach((observer) => observer.disconnect());
      try { pf.destroy(); } catch (_err) { /* deja detruit */ }
      if (apiRef) apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesByKey, totalPages, naturalWidthPx, naturalHeightPx, spineClass]);

  return (
    <div className="preview-flip-stagewrap">
      <div ref={containerRef} className="preview-flip-stage" />
    </div>
  );
}

export default function BookPreviewFinalLuxe() {
  const { t, i18n } = useTranslation('preview');
  const locale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  const formatEuro = (cents) => (cents == null ? '—' : formatPriceCents(cents, pricingCurrency, locale));
  const { bookId } = useParams();
  const navigate = useNavigate();

  const [book, setBook] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Toutes les pages PRECHARGEES (voir le commentaire d'en-tete) : null tant
  // que le lot n'est pas pret. { cover, backCover, interior: [html, ...] }.
  const [pagesByKey, setPagesByKey] = useState(null);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Etat rapporte par page-flip (voir FlipStage) : seule source de verite
  // sur "quelle page est affichee", jamais un compteur React maintenu a part.
  const [flipState, setFlipState] = useState({ index: 0, orientation: 'landscape', pageCount: 0 });
  const flipApiRef = useRef(null);

  // Prix de chaque format A LA PAGINATION ACTUELLE DU LIVRE (chantier
  // "tarification dynamique", 2026-09-27, §9/§10) — { livret: { bookPriceCents,
  // shippingCents }, ... }. Un SEUL champ de pagination desormais partage par
  // les 3 formats (book.page_count) : changer de format ne touche plus au
  // contenu ni au nombre de pages (voir changeFormatOnly), donc plus besoin
  // d'une pagination differente par format (l'ancien formatOptions/
  // getFormatOptions, retire).
  const [formatPricing, setFormatPricing] = useState({});
  const [pricingCurrency, setPricingCurrency] = useState('EUR');
  const [switchingFormat, setSwitchingFormat] = useState(false);
  // Indication breve de variation de prix au changement de format (§9/§11) —
  // meme mecanique que l'atelier (BookAtelierLuxe.js: priceDelta), disparait
  // toute seule.
  const [priceDelta, setPriceDelta] = useState(null);
  const priceDeltaTimeoutRef = useRef(null);
  const [ordering, setOrdering] = useState(false);
  // Ecran recapitulatif qualite avant commande (cahier des charges v2, §2).
  const [qualityWarnings, setQualityWarnings] = useState([]);
  const [isQualityRecapOpen, setIsQualityRecapOpen] = useState(false);
  const [fixingPhotos, setFixingPhotos] = useState(false);
  const [fixMessage, setFixMessage] = useState('');
  // Conseils (correctif 12) : une banniere calme et FACULTATIVE — jamais un inventaire d'alertes.
  // { sharp: [pageIndex], fold: [pageIndex], text: [pageIndex], empty: [pageIndex] }
  const [tips, setTips] = useState(null);
  const [tipsOpen, setTipsOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data: bookRow, error: bookError }, itemList] = await Promise.all([
        fetchBookRow(bookId),
        listContentItems(bookId)
      ]);
      if (bookError) throw new Error(bookError.message || t('errors.bookNotFound'));
      setBook(bookRow);
      setItems(itemList || []);
    } catch (err) {
      setError(err.message || t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!bookId || !book) return undefined;
    let cancelled = false;
    Promise.all([getPrintQualityCheck(bookId), listPages(bookId)]).then(([check, rows]) => {
      if (cancelled) return;
      const pagesOf = (predicate) => [...new Set((check?.warnings || []).filter(predicate).map((entry) => entry.pageIndex))]
        .filter((index) => index != null).sort((a, b) => a - b);
      const filled = new Set((Array.isArray(rows) ? rows : []).filter((row) => {
        const content = row.content || {};
        return (Array.isArray(content.itemIds) && content.itemIds.some(Boolean))
          || Boolean(content.kind && !['photo', 'texte', 'mixte'].includes(content.kind));
      }).map((row) => row.page_index));
      const empty = [];
      for (let index = 0; index < (book.page_count || 0); index += 1) if (!filled.has(index)) empty.push(index);
      setTips({
        sharp: pagesOf((entry) => entry.kind === 'photo'),
        fold: pagesOf((entry) => entry.kind === 'spread'),
        text: pagesOf((entry) => entry.kind === 'texte'),
        empty
      });
    }).catch(() => { if (!cancelled) setTips(null); });
    return () => { cancelled = true; };
  }, [bookId, book]);

  const handleUpdateBook = async (updates) => {
    const { error: updateError } = await updateBookRow(bookId, updates);
    if (updateError) throw updateError;
    setBook((previous) => ({ ...previous, ...updates }));
  };

  // Atteindre cet ecran = l'apercu a bien ete genere/vu — statut jamais
  // regressif (onlyForward), non bloquant (echec silencieux).
  useEffect(() => {
    if (!book?.id) return;
    applyLifecycleStatus('preview_available', { book, onUpdateBook: handleUpdateBook, onlyForward: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book?.id]);

  const photosCount = useMemo(() => items.filter((item) => item.kind === 'photo').length, [items]);
  const souvenirsCount = useMemo(() => items.filter((item) => item.kind === 'texte').length, [items]);
  const totalPages = book?.page_count || 0;
  const currentFormat = normalizePrintFormat(book?.print_format);
  const currentFormatMeta = PRINT_FORMATS.find((format) => format.id === currentFormat)
    || (isCatalogFormat(currentFormat) ? { id: currentFormat, ...formatDimensions(currentFormat), spine: 'premium' } : PRINT_FORMATS[1]);
  const naturalPageWidthPx = currentFormatMeta.widthMm * PX_PER_MM;
  const naturalPageHeightPx = currentFormatMeta.heightMm * PX_PER_MM;

  // Precharge TOUTES les pages (couverture + interieures + 4e) — necessaire
  // au feuilletage page-flip, qui a besoin de l'integralite du jeu de pages
  // des le montage (voir le commentaire d'en-tete). Se relance a chaque
  // changement de FORMAT (le rendu — dimensions, mise en page — en depend),
  // jamais au simple feuilletage (qui ne fait plus aucun appel reseau).
  useEffect(() => {
    if (!book?.id || !totalPages) return undefined;
    let cancelled = false;
    setPagesLoading(true);
    setPagesByKey(null);
    const load = async () => {
      try {
        const [coverHtml, backCoverHtml, interiorHtmls] = await Promise.all([
          fetchCoverPreviewHtml(book.id, 'front'),
          fetchCoverPreviewHtml(book.id, 'back'),
          mapWithConcurrency(
            Array.from({ length: totalPages }, (_, i) => i),
            6,
            (pageIndex) => fetchInteriorPagePreviewHtml(book.id, pageIndex)
          )
        ]);
        if (cancelled) return;
        setPagesByKey({ cover: coverHtml, backCover: backCoverHtml, interior: interiorHtmls });
      } catch (_err) {
        if (!cancelled) setPagesByKey(null);
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [book?.id, totalPages, book?.print_format]);

  // Prix des 3 formats A LA PAGINATION ACTUELLE DU LIVRE (chantier
  // "tarification dynamique", 2026-09-27, §9/§10) — un seul appel a
  // GET /orders/formats?page_count=X (public, deja utilise par le choix de
  // format initial, CreateBookSansIA.js) au lieu de 3 appels estimatePrice.
  // book.page_count ne bouge plus jamais d'un format a l'autre : changer de
  // format ne recompose plus rien (voir handleChooseFormat plus bas).
  useEffect(() => {
    if (!book?.id || !totalPages) return undefined;
    let cancelled = false;
    const charger = () => {
      listPrintFormats(totalPages)
        .then(({ formats, currency }) => {
          if (cancelled) return;
          setFormatPricing(Object.fromEntries((formats || []).map((entry) => [
            entry.formatId,
            {
              bookPriceCents: entry.currentPriceCents ?? entry.startingPriceCents,
              shippingCents: entry.shippingPriceCents,
              minPages: entry.minPages,
              maxPages: entry.maxPages,
              available: entry.available !== false
            }
          ])));
          setPricingCurrency(currency || 'EUR');
        })
        .catch(() => {});
    };
    charger();
    // Pays choisi dans l'en-tete (chantier international, 2026-10-02) :
    // redemande les tarifs si le visiteur change de pays en cours de route.
    const onCountryChange = () => charger();
    window.addEventListener('bookipix:country-changed', onCountryChange);
    return () => {
      cancelled = true;
      window.removeEventListener('bookipix:country-changed', onCountryChange);
    };
  }, [book?.id, totalPages, currentFormat]);

  useEffect(() => () => {
    if (priceDeltaTimeoutRef.current) clearTimeout(priceDeltaTimeoutRef.current);
  }, []);

  // Affiche brievement "Format Luxe · +11,40 €" puis revient tout seul a
  // l'affichage normal (§9/§11 : "pas besoin d'une grosse animation").
  const showPriceDelta = (delta) => {
    if (priceDeltaTimeoutRef.current) clearTimeout(priceDeltaTimeoutRef.current);
    setPriceDelta(delta);
    priceDeltaTimeoutRef.current = setTimeout(() => setPriceDelta(null), 2600);
  };

  // --- Derive l'affichage (sidebar plein ecran, libelle) depuis flipState --
  const contentPageIndex = contentPageFromFlipIndex(flipState.index, totalPages);
  const isPortrait = flipState.orientation === 'portrait';
  const viewKind = flipState.index <= FLIP_COVER_INDEX ? 'cover'
    : flipState.index >= (flipState.pageCount - 1) && flipState.pageCount > 0 ? 'back-cover'
      : 'spread';
  const spreadInfo = viewKind === 'spread' ? describeContentSpread(contentPageIndex, totalPages) : { leftPage: null, rightPage: null };
  // En mode portrait (ecran etroit), page-flip montre une page a la fois —
  // on la traite comme "droite" pour reutiliser la meme derivation partout.
  const leftPageIndex = isPortrait ? null : spreadInfo.leftPage;
  const rightPageIndex = isPortrait ? contentPageIndex : spreadInfo.rightPage;

  const singleHtml = viewKind === 'cover' ? pagesByKey?.cover : viewKind === 'back-cover' ? pagesByKey?.backCover : null;
  const leftHtml = leftPageIndex != null ? pagesByKey?.interior?.[leftPageIndex] : null;
  const rightHtml = rightPageIndex != null ? pagesByKey?.interior?.[rightPageIndex] : null;
  // Photo etalee sur double page dans la vue courante (le rendu pose .photo-spread) :
  // on affiche l'icone (i) qui explique le leger doublon au centre.
  const viewHasSpreadPhoto = [leftHtml, rightHtml].some((html) => typeof html === 'string' && /<figure[^>]*\bphoto-spread\b/.test(html));

  const canGoPrevious = flipState.index > 0;
  const canGoNext = flipState.pageCount > 0 && flipState.index < flipState.pageCount - 1;
  const goPrevious = useCallback(() => flipApiRef.current?.flipPrev(), []);
  const goNext = useCallback(() => flipApiRef.current?.flipNext(), []);
  // Sauts directs aux deux extremites. Sans eux, atteindre la 4e d'un livre
  // de 30 pages demandait de nombreux clics — et autant pour revenir (retour
  // utilisateur 2026-09-15 : « on est obliges de feuilleter tout l'album »).
  const goToCover = useCallback(() => flipApiRef.current?.turnToPage(0), []);
  const goToBackCover = useCallback(() => {
    if (flipApiRef.current) flipApiRef.current.turnToPage(flipApiRef.current.pageCount - 1);
  }, []);

  // Ajustement/zoom/panoramique du plein ecran : entierement delegue a
  // PageZoomStage (common/PageZoomStage.js, partage avec l'atelier) depuis
  // la refonte du 2026-09-09 ("cahier des charges REFONTE UX DES APERCUS")
  // — remplace l'ancien calcul JS "contain fit" fait a la main. zoom persiste
  // deliberement d'une page a l'autre (voir PageZoomStage.js) : un useState
  // simple suffit, pas de logique de reinitialisation ici.
  const SPREAD_GAP_PX = 16;

  // Change UNIQUEMENT le format (chantier "tarification dynamique",
  // 2026-09-27, §9 : "changer de format ne doit PAS renvoyer l'utilisateur
  // dans l'atelier... le contenu reste exactement identique... seul le
  // produit physique et le prix changent") — changeFormatOnly ne recompose
  // rien et ne touche jamais book.page_count, contrairement a l'ancien
  // chooseFormat (toujours disponible, simplement plus appele depuis cet
  // ecran).
  const handleChooseFormat = async (formatId) => {
    if (formatId === currentFormat || switchingFormat) return;
    setSwitchingFormat(true);
    setError('');
    // Indication breve (§9/§11 : "Format Luxe · +11,40 €") — l'ecart du
    // TOTAL (livre + livraison, la livraison differe aussi selon le format,
    // voir la grille), pas seulement du livre : c'est ce que la ligne
    // "Total" affiche juste en dessous, l'ecart annonce doit correspondre a
    // ce qui bouge vraiment. Les deux prix sont deja connus localement
    // (formatPricing), aucun appel reseau supplementaire necessaire.
    const totalFor = (id) => {
      const entry = formatPricing[id];
      return Number.isFinite(entry?.bookPriceCents) && Number.isFinite(entry?.shippingCents)
        ? entry.bookPriceCents + entry.shippingCents
        : null;
    };
    const before = totalFor(currentFormat);
    const after = totalFor(formatId);
    const formatLabel = PRINT_FORMATS.some((format) => format.id === formatId) ? t(`formats.${formatId}.label`) : ((catalogFormatName(formatId) && catalogFormatName(formatId).nom) || formatId);
    try {
      const { book: updatedBook } = await changeFormatOnly(bookId, formatId);
      setBook((previous) => ({ ...previous, ...updatedBook }));
      if (Number.isFinite(before) && Number.isFinite(after) && before !== after) {
        showPriceDelta({ label: t('priceDelta.formatLabel', { format: formatLabel }), priceCents: after - before });
      }
    } catch (err) {
      setError(err?.message || t('errors.formatChangeFailed'));
    } finally {
      setSwitchingFormat(false);
    }
  };

  // Passage effectif a la commande — extrait pour etre appelable soit
  // directement (aucun avertissement qualite), soit depuis l'ecran
  // recapitulatif via "Continuer quand meme".
  const proceedToCheckout = async () => {
    setOrdering(true);
    try {
      await applyLifecycleStatus('finalized', { book, onUpdateBook: handleUpdateBook });
      navigate(`/book/${bookId}/checkout`);
    } catch (_err) {
      setError(t('errors.checkoutFailed'));
      setOrdering(false);
    }
  };

  // Verrou OBLIGATOIRE avant le paiement (cahier des charges v2, §2) :
  // "Il est impossible d'arriver a l'etape de paiement sans avoir vu
  // l'ecran recapitulatif si au moins une photo est sous le seuil de
  // resolution". C'est ici que le verrou vit (et pas dans l'atelier) parce
  // que c'est le seul passage oblige vers /checkout — on peut arriver sur
  // cet ecran sans avoir ouvert "Terminer mon livre". Jamais bloquant en
  // cas d'echec du controle lui-meme (reseau, etc.) : on laisse alors
  // passer plutot que d'empecher une commande legitime.
  //
  // NE RE-DEMANDE PAS si les MEMES photos ont deja ete vues et confirmees
  // dans l'atelier POUR CE LIVRE (retour utilisateur, 2026-09-29 : "on va
  // enlever l'alerte... vu qu'on l'affiche deja... dans l'atelier") — le
  // verrou lui-meme reste en place (avertissementsDejaAcquittes verifie les
  // itemIds un a un, pas juste "deja vu une fois" : une photo NOUVELLEMENT
  // signalee depuis re-ouvre l'ecran normalement). Voir
  // utils/qualityAcknowledgment.js.
  const handleOrder = async () => {
    setOrdering(true);
    try {
      const check = await getPrintQualityCheck(bookId);
      // Correctif 13 : seules les VRAIES alertes ouvrent une fenetre (photo sous 100 dpi, texte qui deborde).
      // Une photo sur double page est un conseil (affiche sur l'apercu), jamais un obstacle a la commande.
      const serious = (check?.warnings || []).filter((entry) => entry.kind !== 'spread');
      const itemIdsActuels = serious.map((entry) => entry.itemId);
      if (serious.length > 0 && !avertissementsDejaAcquittes(bookId, itemIdsActuels)) {
        setQualityWarnings(serious);
        setFixMessage('');
        setIsQualityRecapOpen(true);
        setOrdering(false);
        return;
      }
      // Passe (avertissements deja acquittes, ou aucun) : memorise/rafraichit
      // quand meme l'acquit, pour couvrir le cas "aucun avertissement"
      // explicitement et garder l'horodatage a jour.
      acquitterAvertissementsQualite(bookId, itemIdsActuels);
    } catch (_err) {
      // Controle indisponible : on continue (voir commentaire ci-dessus).
    }
    await proceedToCheckout();
  };

  const viewLabel = viewKind === 'cover'
    ? t('viewLabel.cover')
    : viewKind === 'back-cover'
      ? t('viewLabel.backCover')
      : (leftPageIndex != null && rightPageIndex != null)
        ? t('viewLabel.spreadPages', { left: leftPageIndex + 1, right: rightPageIndex + 1, total: totalPages })
        : (leftPageIndex != null || rightPageIndex != null)
          ? t('viewLabel.singlePage', { page: (leftPageIndex != null ? leftPageIndex : rightPageIndex) + 1, total: totalPages })
          : '';
  const hasCurrentContent = viewKind === 'spread' ? Boolean(leftHtml || rightHtml) : Boolean(singleHtml);
  // Total = livre + livraison du format actuel (§3 : jamais un montant
  // "livraison incluse" sans le detail juste au-dessus, mais un total unique
  // est bien affiche sur le bouton de commande — §13). null tant que les
  // deux ne sont pas connus, plutot qu'un total partiel trompeur.
  // SEULS LES FORMATS POSSIBLES sont proposes : chaque produit a ses limites de pages (un livre de 18 pages ne peut pas
  // etre imprime en Livret, minimum 30) et ses pays desservis. Le format actuel du livre reste toujours affiche, pour
  // qu'on voie ce qui est choisi (retour utilisateur 2026-10-17). Tant que les limites ne sont pas chargees : lui seul.
  const isOfferable = (formatId) => {
    if (formatId === currentFormat) return true;
    const limits = formatPricing[formatId];
    return Boolean(limits) && limits.available
      && totalPages >= limits.minPages && totalPages <= limits.maxPages;
  };
  const currentBookPriceCents = formatPricing[currentFormat]?.bookPriceCents;
  const currentShippingCents = formatPricing[currentFormat]?.shippingCents;
  const currentTotalCents = Number.isFinite(currentBookPriceCents) && Number.isFinite(currentShippingCents)
    ? currentBookPriceCents + currentShippingCents
    : null;
  // « Les deux pages » veut bien dire les deux : au premier vis-a-vis il n'y
  // a que la page de droite, et la feuille doit alors faire une page de
  // large, pas deux.
  // Pages montrees dans l'apercu plein ecran (une seule pour la couverture / 4e) avec le cote du pli.
  const viewerPages = viewKind === 'spread'
    ? [
      leftPageIndex != null && { key: `p${leftPageIndex}`, html: leftHtml, label: t('stage.leftPage'), fold: 'right' },
      rightPageIndex != null && { key: `p${rightPageIndex}`, html: rightHtml, label: t('stage.rightPage'), fold: 'left' }
    ].filter(Boolean)
    : [{ key: viewKind, html: singleHtml, label: t('fullscreen.fullscreenPreview'), fold: null }];
  const viewerLowRes = viewKind === 'spread' && qualityWarnings.some(
    (entry) => entry.kind !== 'texte' && (entry.pageIndex === leftPageIndex || entry.pageIndex === rightPageIndex) && entry.pageIndex != null
  );
  const photoUpgrades = items
    .filter((item) => item.kind === 'photo')
    .map((photo) => ({ preview: photo.metadata && photo.metadata.previewUrl, original: photo.url }));

  if (loading) {
    return <div className="atelier-loading">{t('loading')}</div>;
  }
  if (!book) {
    return <div className="atelier-loading">{error || t('errors.bookNotFound')}</div>;
  }

  return (
    <div className="preview-final-container">
      <header className="preview-final-header">
        <Link to={`/book/${bookId}/atelier`} className="preview-final-back-link">← {t('header.backToEdit')}</Link>
        <div>
          <h1 className="preview-final-title">{t('header.title')}</h1>
          <p className="preview-final-subtitle">{t('header.subtitle', { pages: totalPages, count: photosCount })}</p>
        </div>
      </header>

      {error && <div className="wizard-error preview-final-error">{error}</div>}

      {/* CONSEILS (correctif 12) : une banniere calme, bleu doux, facultative. Jamais d'orange, jamais d'inventaire. */}
      {tips && (() => {
        const groups = ['sharp', 'fold', 'text', 'empty'].filter((key) => tips[key].length > 0);
        if (groups.length === 0) return null;
        return (
          <div className="preview-final-tips" role="note">
            <div className="preview-final-tips-head">
              <span>{t('tips.summary', { count: groups.length })}</span>
              <button type="button" onClick={() => setTipsOpen((open) => !open)} aria-expanded={tipsOpen}>
                {tipsOpen ? t('tips.hide') : t('tips.show')}
              </button>
            </div>
            {tipsOpen && (
              <ul className="preview-final-tips-list">
                {groups.map((key) => (
                  <li key={key}>
                    <span>{t(`tips.${key}`, { count: tips[key].length })}</span>
                    <button type="button" onClick={() => navigate(`/book/${bookId}/atelier?page=${tips[key][0]}`)}>
                      {t('tips.goToPage', { number: tips[key][0] + 1 })}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })()}

      <div className="preview-final-layout">
        <div className="preview-final-stage">
          {pagesLoading && <p className="preview-final-loading">{t('stage.loadingPage')}</p>}
          <button
            type="button"
            className="preview-final-fullscreen-btn"
            onClick={() => setIsFullscreen(true)}
            disabled={!hasCurrentContent}
            title={t('stage.previewTitle')}
          >
            ⛶ <span>{t('stage.preview')}</span>
          </button>
          {pagesByKey && (
            <FlipStage
              pagesByKey={pagesByKey}
              totalPages={totalPages}
              naturalWidthPx={naturalPageWidthPx}
              naturalHeightPx={naturalPageHeightPx}
              spineClass={`is-${currentFormatMeta.spine}`}
              onFlipChange={setFlipState}
              apiRef={flipApiRef}
            />
          )}
          <div className="preview-final-nav">
            <button
              type="button"
              className="preview-final-nav-jump"
              onClick={goToCover}
              disabled={!canGoPrevious}
              title={t('nav.goToCover')}
              aria-label={t('nav.goToCover')}
            >
              ⇤
            </button>
            <button type="button" className="preview-final-nav-arrow" onClick={goPrevious} disabled={!canGoPrevious} aria-label={t('nav.previousPage')}>
              ‹
            </button>
            <span className="preview-final-nav-label">{viewLabel}</span>
            <button type="button" className="preview-final-nav-arrow" onClick={goNext} disabled={!canGoNext} aria-label={t('nav.nextPage')}>
              ›
            </button>
            <button
              type="button"
              className="preview-final-nav-jump"
              onClick={goToBackCover}
              disabled={!canGoNext}
              title={t('nav.goToBackCover')}
              aria-label={t('nav.goToBackCover')}
            >
              ⇥
            </button>
          </div>
          {viewHasSpreadPhoto && (
            <div className="preview-final-spread-note">
              <SpreadPhotoInfo placement="top" layflat={gutterSafetyMmOf(currentFormat) === 0} />
            </div>
          )}
        </div>

        <aside className="preview-final-sidebar">
          <div className="preview-final-sidebar-section">
            <span className="preview-final-sidebar-label">{t('sidebar.yourBook')}</span>
            <p className="preview-final-book-title">{book.title || t('sidebar.untitledBook')}</p>
            <p className="preview-final-book-stats">
              {t('sidebar.stats', { pages: totalPages, photos: photosCount, memories: souvenirsCount })}
            </p>
          </div>

          <div className="preview-final-sidebar-section">
            <span className="preview-final-sidebar-label">{t('sidebar.yourFormat')}</span>
            {/* Pagination IDENTIQUE pour les 3 formats desormais (retour
                utilisateur, 2026-09-27, §9) : changer de format ne recompose
                plus rien, book.page_count ne bouge jamais d'une carte a
                l'autre — plus de warning "pas assez/trop de contenu pour ce
                format" (ancien mecanisme, lie a la recomposition retiree). */}
            <div className="preview-final-format-list">
              {PRINT_FORMATS.filter((format) => isOfferable(format.id)).map((format) => (
                <button
                  key={format.id}
                  type="button"
                  className={`preview-final-format-card ${currentFormat === format.id ? 'is-selected' : ''}`}
                  onClick={() => handleChooseFormat(format.id)}
                  disabled={switchingFormat}
                >
                  <span
                    className={`preview-final-format-spine is-${format.spine}`}
                    style={{ '--spine-thickness': `${Math.min(10, 2 + (totalPages || 16) / 8)}px` }}
                    aria-hidden="true"
                  />
                  <span className="preview-final-format-name">{t(`formats.${format.id}.label`)} {format.badge || ''}</span>
                  <span className="preview-final-format-tagline">{t(`formats.${format.id}.tagline`)}</span>
                  <span className="preview-final-format-description">{t(`formats.${format.id}.description`)}</span>
                  <span className="preview-final-format-dimensions">{format.dimensions} · {t(`formats.${format.id}.coverLabel`)}</span>
                  {/* Meme pagination pour les 3 cartes (retour utilisateur,
                      §9) — affichee quand meme sur chacune : confirme
                      visuellement qu'elle ne bouge pas d'un format a l'autre. */}
                  <span className="preview-final-format-pagecount">{t('sidebar.pagesCount', { count: totalPages })}</span>
                  <span className="preview-final-format-price">{formatEuro(formatPricing[format.id]?.bookPriceCents)}</span>
                </button>
              ))}
              {/* Produits du catalogue (reserves aux testeurs : le serveur ne les annonce qu'a leur compte). */}
              {listCatalogFormats().filter((format) => isOfferable(format.id)).map((format) => (
                <button
                  key={format.id}
                  type="button"
                  className={`preview-final-format-card ${currentFormat === format.id ? 'is-selected' : ''}`}
                  onClick={() => handleChooseFormat(format.id)}
                  disabled={switchingFormat}
                >
                  <span
                    className={`preview-final-format-spine is-${format.reliure === 'rigide' ? 'premium' : 'soft'}`}
                    style={{ '--spine-thickness': `${Math.min(10, 2 + (totalPages || 16) / 8)}px` }}
                    aria-hidden="true"
                  />
                  <span className="preview-final-format-name">{format.nom}</span>
                  {format.accroche && <span className="preview-final-format-tagline">{format.accroche}</span>}
                  <span className="preview-final-format-dimensions">
                    {formatCm(format.widthMm, i18n.language)} × {formatCm(format.heightMm, i18n.language)} cm · {format.reliure === 'rigide' ? t('formats.luxe.coverLabel') : t('formats.standard.coverLabel')}
                  </span>
                  <span className="preview-final-format-pagecount">{t('sidebar.pagesCount', { count: totalPages })}</span>
                  <span className="preview-final-format-price">{formatEuro(formatPricing[format.id]?.bookPriceCents)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Prix detaille (§13/§14 : "a la fin, le prix devient un element
              central") — livre et livraison distingues, jamais un seul
              montant "livraison incluse" (§3). */}
          <div className="preview-final-sidebar-section preview-final-total">
            {priceDelta && (
              <p className="preview-final-price-delta">
                {priceDelta.label} · {formatEurosDelta(priceDelta.priceCents, pricingCurrency, locale)}
              </p>
            )}
            <div className="preview-final-price-line">
              <span>{t('sidebar.yourBookLine')}</span>
              <span>{formatEuro(currentBookPriceCents)}</span>
            </div>
            <div className="preview-final-price-line">
              <span>{t('sidebar.delivery')}</span>
              <span>{formatEuro(currentShippingCents)}</span>
            </div>
            <div className="preview-final-price-line preview-final-price-line-total">
              <span className="preview-final-sidebar-label">{t('sidebar.total')}</span>
              <p className="preview-final-total-price">{formatEuro(currentTotalCents)}</p>
            </div>
          </div>

          <button type="button" className="btn btn-primary preview-final-order-btn" onClick={handleOrder} disabled={ordering}>
            {ordering ? t('sidebar.ordering') : `${t('sidebar.orderButton')} → ${formatEuro(currentTotalCents)}`}
          </button>
        </aside>
      </div>

      <PrintQualityRecapModal
        isOpen={isQualityRecapOpen}
        warnings={qualityWarnings}
        loading={ordering}
        fixing={fixingPhotos}
        fixMessage={fixMessage}
        // « Les afficher plus petites » : un clic, puis on recontrole — ce qui est regle disparait de la liste.
        onFitWhole={async () => {
          setFixingPhotos(true);
          try {
            const result = await fitFlaggedPhotosWhole(bookId, qualityWarnings);
            const check = await getPrintQualityCheck(bookId);
            const serious = (check?.warnings || []).filter((entry) => entry.kind !== 'spread');
            setQualityWarnings(serious);
            setFixMessage(result.fixed > 0 ? t('recap.fixed', { count: result.fixed }) : t('recap.nothingFixed'));
          } catch (_err) {
            setFixMessage(t('recap.fixFailed'));
          } finally {
            setFixingPhotos(false);
          }
        }}
        onClose={() => setIsQualityRecapOpen(false)}
        onContinueAnyway={() => {
          // Meme si le paiement echoue et que l'utilisateur reclique
          // "Commander", inutile de reposer la meme question pour les
          // memes photos.
          acquitterAvertissementsQualite(bookId, qualityWarnings.map((entry) => entry.itemId));
          setIsQualityRecapOpen(false);
          proceedToCheckout();
        }}
        onReviewPage={(pageIndex) => {
          setIsQualityRecapOpen(false);
          navigate(`/book/${bookId}/atelier?page=${pageIndex}`);
        }}
      />

      {isFullscreen && (
        // APERCU PLEIN ECRAN (2026-10-12) : composant partage avec l'atelier, voir common/PagePreviewViewer.js.
        <PagePreviewViewer
          title={book?.title || t('header.title')}
          pages={viewerPages}
          pageWidthPx={naturalPageWidthPx}
          pageHeightPx={naturalPageHeightPx}
          spineGapPx={SPREAD_GAP_PX}
          counter={viewLabel}
          canPrev={canGoPrevious}
          canNext={canGoNext}
          onPrev={goPrevious}
          onNext={goNext}
          onFirst={goToCover}
          onLast={goToBackCover}
          onClose={() => setIsFullscreen(false)}
          pageWidthMm={currentFormatMeta.widthMm}
          marginMm={getPageMetrics(currentFormat).paddingMm}
          foldMm={gutterSafetyMmOf(currentFormat)}
          lowRes={viewerLowRes}
          photoUpgrades={photoUpgrades}
          spreadPhoto={viewHasSpreadPhoto}
        />
      )}
    </div>
  );
}
